import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAppData } from '../../core/contexts/AppDataContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { formatBrandMoney, getBrandLocale, getBrandVatRate } from '../../shared/utils/brandCurrency';
import { SAUDI_VOUCHER_TYPES } from './saudiTreasuryDefaults';
import { mapVoucherFromApi, mapVoucherToApi } from './treasuryApiMappers';
import {
  voucherExportHeaders,
  mapVouchersForExport,
  exportTreasuryCSV,
  exportTreasuryExcel,
  exportTreasuryPDF,
  voucherImportHeaders,
  triggerTreasuryImport,
  parseTreasuryImport,
} from './treasuryExportHelpers';
import {
  Plus,
  Search,
  FileText,
  Wallet,
  File,
  Download,
  Calendar,
  TrendingUp,
  CheckCircle,
  Filter as FilterIcon,
  Printer,
  ChevronDown,
  Upload,
  FileSpreadsheet,
  List,
  Grid,
  LayoutGrid,
  Shield,
  Layers,
  RefreshCcw,
  X,
  ChevronUp,
  DollarSign,
  LayoutList,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileOutput,
  Clock,
  XCircle,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { aiAgentService as aiService } from '../../ai-agent';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  QAWAEM_MODAL_OVERLAY_CLASS,
  getQawaemModalPanelClass,
  getQawaemModalPanelStyle,
} from '../../products/saudi/components/QawaemErpCrudModal';
import { QawaemErpModalFormTabBar } from '../../products/saudi/components/QawaemErpModalFormTabs';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { buildCaisseColumnDefs, useCaisseColumnVisibility, caisseTableColSpan, buildCaisseModalFormTabs, nextCaisseEntityRef, sliceCaisseListItems, CAISSE_FORM_GRID } from './caisseListPageUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useTreasuryConfig, useTreasuryListViewMode } from './useTreasuryConfig';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';

const GestionBonsPage = () => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const treasuryConfig = useTreasuryConfig();
  const [viewMode, setViewMode] = useTreasuryListViewMode(treasuryConfig);
  const p = (key, opts) => t(`modules.caisse.pages.gestionBonsPage.${key}`, opts);
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const dateLocale = getBrandLocale(i18n.language);
  const formatAmount = (amount) => formatBrandMoney(amount);
  const defaultVatRate = getBrandVatRate();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'السند' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'السندات' });

  const { addBonCommande, getBons, getCustomers, getSuppliers } = useAppData();
  
  const [searchTerm, setSearchTerm] = useState('');
  const { filters: columnFilters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [filterType, setFilterType] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [bons, setBons] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [editingBon, setEditingBon] = useState(null);
  const [selectedBon, setSelectedBon] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [defaultCaisseId, setDefaultCaisseId] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // New UI Design States
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [mainViewMode, setMainViewMode] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  
  // Date range filter
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  const [bonFormTab, setBonFormTab] = useState('general');
  const bonFormTabDefs = useMemo(() => ([
    { id: 'general', label: p('section_general'), icon: FileText, iconClassName: 'text-emerald-400' },
    { id: 'amounts', label: p('section_amounts'), icon: DollarSign, iconClassName: 'text-amber-400' },
  ]), [p]);
  const BON_FIELD_COUNT = 10;
  const bonFormTabs = useMemo(
    () => buildCaisseModalFormTabs(bonFormTabDefs, BON_FIELD_COUNT),
    [bonFormTabDefs],
  );

  const openAddFormModal = () => {
    setBonFormTab('general');
    setEditingBon(null);
    setNewBon({
      date: new Date().toISOString().split('T')[0],
      beneficiaire: '',
      typeBon: 'petty_cash',
      designation: '',
      articles: '',
      montantHT: '',
      tva: '',
      montantTTC: '',
      notes: '',
      documentFile: null,
      documentPreview: null,
      invoiceNumber: nextCaisseEntityRef(
        settings,
        'voucher',
        treasuryConfig.voucherPrefix || 'BON',
        bons,
        (bon) => bon.invoiceNumber,
      ),
    });
    setShowAddForm(true);
  };

  const closeAddFormModal = () => {
    setBonFormTab('general');
    setEditingBon(null);
    setShowAddForm(false);
  };
  
  // Form state for new Bon
  const [newBon, setNewBon] = useState({
    date: new Date().toISOString().split('T')[0],
    beneficiaire: '',
    typeBon: 'petty_cash',
    designation: '',
    articles: '',
    montantHT: '',
    tva: '',
    montantTTC: '',
    notes: '',
    documentFile: null,
    documentPreview: null,
    invoiceNumber: '',
  });
  
  // AI extraction state
  const [isExtractingDocument, setIsExtractingDocument] = useState(false);
  const [extractionConfidence, setExtractionConfidence] = useState(null);
  
  const clients = getCustomers();
  const fournisseurs = getSuppliers();

  const resolveBeneficiaryName = (value) => {
    if (!value) return '';
    if (value.startsWith('client-')) {
      const id = value.replace('client-', '');
      return clients.find((c) => String(c.id) === id)?.name || value;
    }
    if (value.startsWith('fournisseur-')) {
      const id = value.replace('fournisseur-', '');
      return fournisseurs.find((f) => String(f.id) === id)?.name || value;
    }
    return value;
  };

  const loadBons = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, dashboard] = await Promise.all([
        localApiAdapter.get('/treasury/vouchers'),
        localApiAdapter.get('/tresorerie/position/by-type/caisse').catch(() => null),
      ]);
      const principal = (dashboard?.accounts || dashboard?.balanceByCaisse || []).find((c) => c.type === 'principal');
      if (principal?.id) setDefaultCaisseId(principal.id);
      setBons(sortRecordsNewestFirst((Array.isArray(rows) ? rows : []).map(mapVoucherFromApi)));
    } catch (error) {
      console.error('Failed to load vouchers:', error);
      toast.error(p('toast_create_error'));
      setBons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBons();
  }, [loadBons]);

  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Handle outside clicks for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  
  // Handle document upload and AI extraction
  const handleDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('📄 Document upload started:', file.name, file.type, file.size);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(p('toast_image_required'));
      return;
    }

    // Create preview
    const previewUrl = URL.createObjectURL(file);
    
    setNewBon(prev => ({
      ...prev,
      documentFile: file,
      documentPreview: previewUrl
    }));

    // Extract data with AI
    setIsExtractingDocument(true);
    toast.loading(p('toast_extracting'), { id: 'ai-extract' });

    console.log('🤖 Starting AI extraction...');

    try {
      const result = await aiService.extractDocumentData(file, 'bon_commande');
      
      console.log('📊 AI extraction result:', result);

      if (result.success) {
        const extracted = result.extractedData;
        
        console.log('✅ Extracted data:', extracted);
        
        // Auto-fill form fields
        setNewBon(prev => ({
          ...prev,
          date: extracted.date || prev.date,
          beneficiaire: extracted.fournisseurId || prev.beneficiaire,
          designation: extracted.designation || prev.designation,
          articles: extracted.articles || prev.articles,
          montantHT: extracted.montantHT || prev.montantHT,
          tva: extracted.tva || prev.tva,
          montantTTC: extracted.montantTTC || prev.montantTTC,
          notes: extracted.notes || prev.notes
        }));

        setExtractionConfidence(result.confidence);
        
        const confidenceMsg = result.model 
          ? p('toast_extract_success_model', { model: result.model, percent: (result.confidence * 100).toFixed(0) })
          : p('toast_extract_success', { percent: (result.confidence * 100).toFixed(0) });
        
        toast.success(confidenceMsg, { id: 'ai-extract' });
      } else {
        console.error('❌ AI extraction failed:', result.error);
        toast.error(p('toast_extract_error', { error: result.error }), { id: 'ai-extract' });
      }
    } catch (error) {
      console.error('💥 Document extraction exception:', error);
      toast.error(p('toast_extract_failed'), { id: 'ai-extract' });
    } finally {
      setIsExtractingDocument(false);
      console.log('🏁 AI extraction finished');
    }
  };

  // Remove document
  const handleRemoveDocument = () => {
    if (newBon.documentPreview) {
      URL.revokeObjectURL(newBon.documentPreview);
    }
    setNewBon(prev => ({
      ...prev,
      documentFile: null,
      documentPreview: null
    }));
    setExtractionConfidence(null);
  };

  // Statistiques
  const stats = {
    totalBons: bons.length,
    totalAmount: bons.reduce((sum, bon) => sum + bon.total, 0),
    pendingAmount: bons.filter(bon => bon.status === 'pending').reduce((sum, bon) => sum + bon.total, 0),
    completedAmount: bons.filter(bon => bon.status === 'completed').reduce((sum, bon) => sum + bon.total, 0)
  };

  // Distribution des bons (Saudi voucher types)
  const bonsDistribution = [
    { nameKey: 'dist_petty_cash', count: bons.filter(bon => bon.type === 'petty_cash').length, color: 'from-blue-400 to-blue-500' },
    { nameKey: 'dist_expense', count: bons.filter(bon => bon.type === 'expense').length, color: 'from-amber-400 to-amber-500' },
    { nameKey: 'dist_advance', count: bons.filter(bon => bon.type === 'advance').length, color: 'from-purple-400 to-purple-500' },
  ];

  const columnFilterRules = useMemo(() => [
    textColumnRule('number', (bon) => bon.invoiceNumber || ''),
    textColumnRule('date', (bon) => new Date(bon.date).toLocaleDateString(dateLocale)),
    textColumnRule('beneficiary', (bon) => bon.clientName || ''),
    textColumnRule('type', (bon) => bon.type || ''),
    textColumnRule('amount', (bon) => formatAmount(bon.total)),
    exactColumnRule('status', (bon) => bon.status || ''),
  ], [dateLocale, formatAmount]);

  const filteredBons = useMemo(() => {
    const base = bons.filter((bon) => {
      if (activeTab !== 'all') {
        if (activeTab === 'completed' && bon.status !== 'completed') return false;
        if (activeTab === 'pending' && bon.status !== 'pending') return false;
        if (activeTab === 'petty_cash' && bon.type !== 'petty_cash') return false;
        if (activeTab === 'expense' && bon.type !== 'expense') return false;
        if (activeTab === 'advance' && bon.type !== 'advance') return false;
      }
      if (filterType !== 'all') {
        if (filterType === 'completed' && bon.status !== 'completed') return false;
        if (filterType === 'pending' && bon.status !== 'pending') return false;
        if (filterType === 'petty_cash' && bon.type !== 'petty_cash') return false;
        if (filterType === 'expense' && bon.type !== 'expense') return false;
        if (filterType === 'advance' && bon.type !== 'advance') return false;
      }
      if (!searchTerm) return true;
      return bon.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bon.clientName.toLowerCase().includes(searchTerm.toLowerCase());
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [bons, activeTab, filterType, searchTerm, columnFilters, columnFilterRules]);

  const filteredRowIds = useMemo(() => filteredBons.map((r) => r.id), [filteredBons]);
  const allRowsSelected = filteredRowIds.length > 0 && filteredRowIds.every((id) => selectedItems.includes(id));
  const someRowsSelected = selectedItems.length > 0 && !allRowsSelected;
  

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    ;
  };

  const handleView = (bon) => {
    setSelectedBon(bon);
    setShowViewModal(true);
  };

  const handleEdit = (bon) => {
    setEditingBon(bon);
    setNewBon({
      date: bon.date,
      beneficiaire: bon.clientName,
      typeBon: bon.type,
      designation: bon.designation || '',
      articles: '',
      montantHT: String(bon.total),
      tva: '',
      montantTTC: String(bon.total),
      notes: bon.designation || '',
      documentFile: null,
      documentPreview: null,
      invoiceNumber: bon.invoiceNumber,
    });
    setBonFormTab('general');
    setShowAddForm(true);
    toast.success(p('toast_edit_voucher', { number: bon.invoiceNumber }));
  };

  const handleDelete = async (bonId) => {
    if (!window.confirm(p('confirm_delete'))) return;
    try {
      await localApiAdapter.delete(`/treasury/vouchers/${bonId}`);
      notifyDeleted(showNotify, entityLabel);
      await loadBons();
    } catch (error) {
      notifyError(showNotify, p('toast_delete_error'));
      console.error(error);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedItems.length || !window.confirm(p('confirm_delete'))) return;
    try {
      await Promise.all(selectedItems.map((id) => localApiAdapter.delete(`/treasury/vouchers/${id}`)));
      const deleteCount = selectedItems.length;
      setSelectedItems([]);
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      await loadBons();
    } catch (error) {
      notifyError(showNotify, p('toast_delete_error'));
    }
  };

  const clickableStatusOptions = useMemo(() => ([
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'completed', label: p('status_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
  ]), [p]);

  const bonsColumnKeys = useMemo(
    () => ['number', 'date', 'beneficiary', 'type', 'amount', 'status', 'actions'],
    [],
  );
  const pagedBons = useMemo(
    () => sliceCaisseListItems(filteredBons, treasuryConfig.itemsPerPage),
    [filteredBons, treasuryConfig.itemsPerPage],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useCaisseColumnVisibility(bonsColumnKeys);
  const qawaemColumns = useMemo(
    () => buildCaisseColumnDefs([
      { key: 'number', labelKey: 'col_number' },
      { key: 'date', labelKey: 'col_date' },
      { key: 'beneficiary', labelKey: 'col_beneficiary' },
      { key: 'type', labelKey: 'col_type' },
      { key: 'amount', labelKey: 'col_amount' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = caisseTableColSpan(qawaemColumns);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/treasury/vouchers/${recordId}`, { status: newStatus });
      notifyUpdated(showNotify, entityLabel);
      await loadBons();
    } catch (error) {
      console.error('Voucher status change error:', error);
      notifyError(showNotify, error.message || t('common.error'));
    }
  };

  const handleDownload = (bon) => {
    try {
      toast.success(p('toast_download_started', { number: bon.invoiceNumber }));
    } catch (error) {
      toast.error(p('toast_error', { message: error.message }));
    }
  };
  
  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredRowIds);
    }
  };

  const handleItemSelect = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };
  
  const mainTabs = [
    { id: 'all', nameKey: 'tab_general', icon: LayoutGrid },
    { id: 'par_type', nameKey: 'tab_by_type', icon: Shield },
    { id: 'par_statut', nameKey: 'tab_by_status', icon: Layers }
  ];
  
  const quickFilterButtons = [
    { id: 'all', nameKey: 'filter_all', icon: FileText },
    { id: 'completed', nameKey: 'filter_completed', icon: CheckCircle },
    { id: 'pending', nameKey: 'filter_pending', icon: Calendar }
  ];

  const tabs = [
    { id: 'all', nameKey: 'tab_all', icon: FileText },
    { id: 'completed', nameKey: 'tab_completed', icon: CheckCircle },
    { id: 'pending', nameKey: 'tab_pending', icon: Calendar },
    { id: 'petty_cash', nameKey: 'tab_petty_cash', icon: File },
    { id: 'expense', nameKey: 'tab_expense', icon: Wallet },
    { id: 'advance', nameKey: 'tab_advance', icon: FileText },
  ];

  const statCards = [
    { titleKey: 'stat_total', value: stats.totalBons, icon: FileText, color: 'from-blue-500 to-blue-600' },
    { titleKey: 'stat_total_ordered', value: formatAmount(stats.totalAmount), icon: DollarSign, color: 'from-green-500 to-green-600' },
    { titleKey: 'stat_completed_amount', value: formatAmount(stats.completedAmount), icon: CheckCircle, color: 'from-emerald-500 to-emerald-600' },
    { titleKey: 'stat_pending_amount', value: formatAmount(stats.pendingAmount), icon: Clock, color: 'from-amber-500 to-amber-600' },
    { titleKey: 'stat_advance', value: bons.filter(b => b.type === 'advance').length, icon: FileText, color: 'from-purple-500 to-purple-600' }
  ];

  const qawaemStats = useMemo(() => sliceQawaemStats(
    statCards.slice(0, 4).map((s) => ({
      key: s.titleKey,
      label: p(s.titleKey),
      value: s.value,
      icon: s.icon,
      variant: 'copper',
    })),
    4,
  ), [statCards, p]);

  const tabFilterOptions = useMemo(() => tabs.map((tab) => ({
    value: tab.id,
    label: p(tab.nameKey),
  })), [tabs, p]);

  const tabFilterLabel = tabFilterOptions.find((o) => o.value === activeTab)?.label || p('tab_all');
  
  const handleSubmitNewBon = async (e) => {
    e.preventDefault();
    try {
      const beneficiary = resolveBeneficiaryName(newBon.beneficiaire);
      const generatedNumber = editingBon?.invoiceNumber || newBon.invoiceNumber || nextCaisseEntityRef(
        settings,
        'voucher',
        treasuryConfig.voucherPrefix || 'BON',
        bons,
        (bon) => bon.invoiceNumber,
      );
      const payload = mapVoucherToApi(
        { ...newBon, beneficiaire: beneficiary },
        {
          caisseId: editingBon?.caisse_id || defaultCaisseId,
          invoiceNumber: generatedNumber,
        }
      );

      if (editingBon) {
        await localApiAdapter.put(`/treasury/vouchers/${editingBon.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/treasury/vouchers', payload);
        notifyCreated(showNotify, entityLabel);
      }
      if (newBon.documentPreview) URL.revokeObjectURL(newBon.documentPreview);
      setBonFormTab('general');
      closeAddFormModal();
      setNewBon({
        date: new Date().toISOString().split('T')[0],
        beneficiaire: '',
        typeBon: 'petty_cash',
        designation: '',
        articles: '',
        montantHT: '',
        tva: '',
        montantTTC: '',
        notes: '',
        documentFile: null,
        documentPreview: null,
        invoiceNumber: '',
      });
      setExtractionConfidence(null);
      await loadBons();
    } catch (error) {
      notifyError(showNotify, p('toast_create_error'));
      console.error(error);
    }
  };

  const exportRows = mapVouchersForExport(filteredBons);
  const exportHeaders = voucherExportHeaders(p);
  const exportBaseName = `treasury_vouchers_${new Date().toISOString().slice(0, 10)}`;

  const handleExportCSV = () => {
    if (!exportRows.length) { toast.error(p('empty_message')); return; }
    exportTreasuryCSV(exportRows, exportHeaders, `${exportBaseName}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    if (!exportRows.length) { toast.error(p('empty_message')); return; }
    await exportTreasuryExcel(exportRows, exportHeaders, `${exportBaseName}.xlsx`, p('title'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    if (!exportRows.length) { toast.error(p('empty_message')); return; }
    setIsGeneratingPDF(true);
    try {
      await exportTreasuryPDF(p('title'), exportHeaders, exportRows);
    } finally {
      setIsGeneratingPDF(false);
      setShowExportDropdown(false);
    }
  };

  const handleImportFile = (file) => {
    const headers = voucherImportHeaders(p);
    const schema = {
      requiredFields: ['date', 'clientName', 'total'],
      validators: { total: (v) => !Number.isNaN(Number(v)) },
    };
    parseTreasuryImport(file, headers, schema)
      .then(async (result) => {
        if (!result.success) {
          toast.error(result.errors?.[0] || p('toast_create_error'));
          return;
        }
        let imported = 0;
        for (const row of result.data) {
          const invoiceNumber = nextCaisseEntityRef(
            settings,
            'voucher',
            treasuryConfig.voucherPrefix || 'BON',
            bons,
            (bon) => bon.invoiceNumber,
          );
          const payload = mapVoucherToApi(
            {
              date: row.date,
              beneficiaire: row.clientName,
              montantTTC: row.total,
              designation: row.designation || '',
              typeBon: row.type === 'expense' ? 'expense' : row.type === 'advance' ? 'advance' : 'petty_cash',
            },
            { caisseId: defaultCaisseId, invoiceNumber }
          );
          await localApiAdapter.post('/treasury/vouchers', payload);
          imported += 1;
        }
        toast.success(p('import_success', { count: imported }));
        await loadBons();
      })
      .catch((err) => {
        console.error('Import vouchers error:', err);
        toast.error(p('toast_create_error'));
      })
      .finally(() => setShowImportDropdown(false));
  };

  return (
    <>
    <QawaemErpListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Wallet}
      stats={qawaemStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={selectedItems.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{t('common.delete')} ({selectedItems.length})</span>
        </button>
      ) : null}
      searchTerm={searchTerm}
      onSearchChange={(v) => { setSearchTerm(v); ; }}
      searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
      onCreate={openAddFormModal}
      createLabel={p('new_voucher')}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import'),
        exportLabel: p('export'),
        generatingLabel: t('common.loading'),
        isGeneratingPDF,
        onImportCSV: () => { triggerTreasuryImport(handleImportFile, '.csv'); setShowImportDropdown(false); },
        onImportExcel: () => { triggerTreasuryImport(handleImportFile); setShowImportDropdown(false); },
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      }}
    >
      {/* Tabs Navigation */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
          {tabs.map((tab) => (
            <button 
              key={tab.id} 
              onClick={() => { setActiveTab(tab.id); ; }} 
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                activeTab === tab.id 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' 
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Distribution des bons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <h3 className="text-xl font-semibold text-white mb-4">
          {p('distribution_title')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {bonsDistribution.map((item) => (
            <div 
              key={item.nameKey}
              className="bg-white/5 rounded-xl p-4 border border-white/10"
            >
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-white">{p(item.nameKey)}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 bg-gradient-to-r ${item.color} text-white`}>
                  {item.count}
                </div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full">
                <div 
                  className={`h-3 rounded-full bg-gradient-to-r ${item.color}`}
                  style={{ width: `${(item.count / bons.length * 100).toFixed(1)}%` }}
                ></div>
              </div>
              <p className="mt-2 text-sm text-white/60">
                {p('percent_of_vouchers', { percent: (item.count / bons.length * 100).toFixed(1) })}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Search and Filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {shouldShowCardsToolbarSearch(viewMode) ? (
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-5 w-5 text-white/50" />
              <input
                type="text"
                placeholder={p('search_placeholder')}
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ) : null}

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <FilterIcon className="h-5 w-5 text-white/50" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{p('filter_all_statuses')}</option>
              <option value="completed">{p('filter_completed')}</option>
              <option value="pending">{p('filter_pending_lower')}</option>
              <option value="petty_cash">{p('filter_petty_cash')}</option>
              <option value="expense">{p('filter_expense')}</option>
              <option value="advance">{p('filter_advance')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bons List */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {filteredBons.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <FileText className="h-16 w-16 text-white/30 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">{p('empty_title')}</h3>
            <p className="text-white/60 mb-6">{p('empty_message')}</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setActiveTab('all');
                setFilterType('all');
              }}
              className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
            >
              <File className="h-5 w-5" />
              <span>{p('reset_filters')}</span>
            </button>
          </div>
        ) : (
          <>
            <div className="glass-table-scroll overflow-x-auto">
              <table className="w-full">
                <thead className="glass-table-head">
                  <tr>
                    <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                      <div className="flex justify-center">
                        <QawaemTableCheckbox
                          size="lg"
                          checked={allRowsSelected}
                          indeterminate={someRowsSelected}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    {isColumnVisible('number') ? (
                      <TableColumnHeaderFilter columnKey="number" label={p('col_number')} value={columnFilters.number || ''} onChange={(v) => setFilter('number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('date') ? (
                      <TableColumnHeaderFilter columnKey="date" label={p('col_date')} value={columnFilters.date || ''} onChange={(v) => setFilter('date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('beneficiary') ? (
                      <TableColumnHeaderFilter columnKey="beneficiary" label={p('col_beneficiary')} value={columnFilters.beneficiary || ''} onChange={(v) => setFilter('beneficiary', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('type') ? (
                      <TableColumnHeaderFilter columnKey="type" label={p('col_type')} value={columnFilters.type || ''} onChange={(v) => setFilter('type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('amount') ? (
                      <TableColumnHeaderFilter columnKey="amount" label={p('col_amount')} value={columnFilters.amount || ''} onChange={(v) => setFilter('amount', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('status') ? (
                      <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={[{ value: 'completed', label: p('filter_completed') }, { value: 'pending', label: p('filter_pending_lower') }]} value={columnFilters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    <QawaemTableHeaderActionsCell
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                      label={p('col_actions')}
                      columns={qawaemColumns}
                      onToggle={handleToggleColumn}
                      visibleCount={qawaemVisibleColumnCount}
                      totalCount={qawaemColumns.length}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {pagedBons.map((bon) => (
                    <tr 
                      key={bon.id} 
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedItems.includes(bon.id)}
                            onChange={() => handleItemSelect(bon.id)}
                          />
                        </div>
                      </td>
                      {isColumnVisible('number') ? (
                        <td className="px-6 py-4 text-white font-mono text-sm">{bon.invoiceNumber}</td>
                      ) : null}
                      {isColumnVisible('date') ? (
                        <td className="px-6 py-4 text-white/80">
                          {new Date(bon.date).toLocaleDateString(dateLocale, {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </td>
                      ) : null}
                      {isColumnVisible('beneficiary') ? (
                        <td className="px-6 py-4 text-white font-medium">{bon.clientName}</td>
                      ) : null}
                      {isColumnVisible('type') ? (
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            bon.type === 'petty_cash'
                              ? 'bg-blue-500/20 text-blue-400'
                              : bon.type === 'expense'
                                ? 'bg-amber-500/20 text-amber-400'
                                : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {bon.type === 'petty_cash'
                              ? p('type_petty_cash')
                              : bon.type === 'expense'
                                ? p('type_expense')
                                : bon.type === 'advance'
                                  ? p('type_advance')
                                  : p('type_expense')}
                          </span>
                        </td>
                      ) : null}
                      {isColumnVisible('amount') ? (
                        <td className="px-6 py-4 text-white font-medium">
                          {formatAmount(bon.total)}
                        </td>
                      ) : null}
                      {isColumnVisible('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={bon.status}
                              recordId={bon.id}
                              options={clickableStatusOptions}
                              onChange={handleStatusChange}
                              title={t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                            />
                          </div>
                        </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-1">
                          <QawaemErpRowActions
                            onView={() => handleView(bon)}
                            onEdit={() => handleEdit(bon)}
                            onDelete={() => handleDelete(bon.id)}
                            viewTitle={p('action_view')}
                            editTitle={p('action_edit')}
                            deleteTitle={p('action_delete')}
                          />
                          <button
                            onClick={() => handleDownload(bon)}
                            className="p-2 rounded-lg text-purple-400 hover:text-purple-300 hover:bg-white/10 transition-colors"
                            title={p('action_download')}
                          >
                            <Download size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      
      {/* Modal Nouveau Bon */}
      {showAddForm && ReactDOM.createPortal(
        <div className={QAWAEM_MODAL_OVERLAY_CLASS} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={getQawaemModalPanelClass(isLight)}
            style={getQawaemModalPanelStyle(isLight, '1200px')}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{editingBon ? p('action_edit') : p('modal_new_title')}</h2>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  form="bon-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {p('save')}
                </button>
                <button type="button" onClick={closeAddFormModal} className="text-white/70 hover:text-white transition-colors" aria-label={p('close_aria')}>
                  <X size={24} />
                </button>
              </div>
            </div>
            {bonFormTabs.length > 0 ? (
              <QawaemErpModalFormTabBar tabs={bonFormTabs} activeTab={bonFormTab} onTabChange={setBonFormTab} isLight={isLight} ariaLabel={editingBon ? p('action_edit') : p('modal_new_title')} />
            ) : null}
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="bon-form" onSubmit={handleSubmitNewBon}>
                
                {(bonFormTabs.length === 0 || bonFormTab === 'general') && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                              <label className="block w-full text-white/70 text-sm font-medium text-right">{p('col_number')} <span className="qawaem-required-mark">*</span></label>
                              <input
                                type="text"
                                required
                                value={newBon.invoiceNumber || ''}
                                onChange={(e) => setNewBon({ ...newBon, invoiceNumber: e.target.value })}
                                placeholder={p('col_number')}
                                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                              />
                            </div>
                    <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                              <label className="block w-full text-white/70 text-sm font-medium text-right">{p('label_date')} <span className="qawaem-required-mark">*</span></label>
                              <CustomDatePicker
                                value={newBon.date}
                                onChange={(date) => setNewBon({ ...newBon, date })}
                                placeholder={p('placeholder_date')}
                              />
                            </div>
                            
                            <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                              <label className="block w-full text-white/70 text-sm font-medium text-right">{p('label_type')} <span className="qawaem-required-mark">*</span></label>
                              <CustomSelect
                                value={newBon.typeBon}
                                onChange={(value) => setNewBon({ ...newBon, typeBon: value })}
                                options={SAUDI_VOUCHER_TYPES.map((opt) => ({
                                  value: opt.value,
                                  label: p(opt.labelKey),
                                }))}
                                placeholder={p('placeholder_type')}
                              />
                            </div>
                            
                            <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                              <label className="block w-full text-white/70 text-sm font-medium text-right">{p('label_beneficiary')} <span className="qawaem-required-mark">*</span></label>
                              <CustomSelect
                                value={newBon.beneficiaire}
                                onChange={(value) => setNewBon({ ...newBon, beneficiaire: value })}
                                options={[
                                  ...clients.filter(c => c.status === 'active').map(client => ({ 
                                    value: `client-${client.id}`, 
                                    label: `${client.name} - ${client.code || p('client_label')}` 
                                  })),
                                  ...fournisseurs.filter(f => f.status === 'active').map(fournisseur => ({ 
                                    value: `fournisseur-${fournisseur.id}`, 
                                    label: `${fournisseur.name} - ${fournisseur.code || p('supplier_label')}` 
                                  }))
                                ]}
                                placeholder={p('placeholder_beneficiary')}
                                searchable={true}
                              />
                            </div>
                            
                            <div className="flex flex-col gap-2 min-w-0 md:col-span-3 md:min-h-[4.25rem]">
                              <label className="block w-full text-white/70 text-sm font-medium text-right">{p('label_designation')}</label>
                              <input
                                type="text"
                                value={newBon.designation}
                                onChange={(e) => setNewBon({ ...newBon, designation: e.target.value })}
                                placeholder={p('placeholder_designation')}
                                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              />
                            </div>
                            
                            {/* Upload Document & AI Extraction */}
                            <div className="flex flex-col gap-2 min-w-0 md:col-span-3">
                              <label className="block w-full text-white/70 text-sm font-medium text-right">
                                {p('label_document')}
                              </label>
                              <div className="flex items-center gap-3">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleDocumentUpload}
                                  className="hidden"
                                  id="bon-document-upload"
                                  disabled={isExtractingDocument}
                                />
                                <label
                                  htmlFor="bon-document-upload"
                                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/20 transition-all cursor-pointer ${
                                    isExtractingDocument 
                                      ? 'bg-white/5 cursor-not-allowed' 
                                      : 'bg-white/10 hover:bg-white/20'
                                  }`}
                                >
                                  <Upload size={18} className="text-blue-400" />
                                  <span className="text-white text-sm">
                                    {isExtractingDocument ? p('upload_extracting') : (newBon.documentFile ? p('upload_change') : p('upload_document'))}
                                  </span>
                                </label>
                                
                                {newBon.documentFile && !isExtractingDocument && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-white/70 text-sm">{newBon.documentFile.name}</span>
                                    <button
                                      type="button"
                                      onClick={handleRemoveDocument}
                                      className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                      title={p('remove_document')}
                                    >
                                      <X size={16} />
                                    </button>
                                  </div>
                                )}
                                
                                {isExtractingDocument && (
                                  <div className="flex items-center gap-2">
                                    <RefreshCcw size={18} className="text-blue-400 animate-spin" />
                                    <span className="text-blue-400 text-sm">{p('ai_analyzing')}</span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Document Preview */}
                              {newBon.documentPreview && (
                                <div className="mt-2 relative inline-block">
                                  <img
                                    src={newBon.documentPreview}
                                    alt={p('preview_alt')}
                                    className="max-h-48 rounded-xl border-2 border-white/20 shadow-lg"
                                  />
                                  {extractionConfidence && (
                                    <div className="absolute top-2 right-2 px-3 py-1 bg-green-500/90 backdrop-blur-sm rounded-lg text-white text-xs font-semibold">
                                      {p('confidence', { percent: (extractionConfidence * 100).toFixed(0) })}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              <p className="text-xs text-white/50">
                                {p('document_hint')}
                              </p>
                            </div>
                    {bonFormTabs.length <= 2 ? (
                      <div className="min-w-0 md:col-span-3">
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_articles')}</label>
                        <textarea value={newBon.articles} onChange={(e) => setNewBon({ ...newBon, articles: e.target.value })} placeholder={p('placeholder_articles')} rows={5} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" />
                      </div>
                    ) : null}
                  </div>
                )}
                {bonFormTabs.length > 2 && bonFormTab === 'articles' && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0 md:col-span-3">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_articles')}</label>
                            <textarea
                              value={newBon.articles}
                              onChange={(e) => setNewBon({ ...newBon, articles: e.target.value })}
                              placeholder={p('placeholder_articles')}
                              rows={5}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                            />
                          </div>
                    </div>
                  </div>
                )}
                {(bonFormTabs.length === 0 || bonFormTab === 'amounts') && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                              <label className="block w-full text-white/70 text-sm font-medium text-right">{p('label_amount_ht')}</label>
                              <input
                                type="number"
                                value={newBon.montantHT}
                                onChange={(e) => {
                                  const ht = parseFloat(e.target.value) || 0;
                                  const tva = parseFloat(newBon.tva) || 0;
                                  const ttc = ht + (ht * tva / 100);
                                  setNewBon({ 
                                    ...newBon, 
                                    montantHT: e.target.value,
                                    montantTTC: ttc.toFixed(2)
                                  });
                                }}
                                placeholder={p('placeholder_amount')}
                                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              />
                            </div>
                            
                            <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                              <label className="block w-full text-white/70 text-sm font-medium text-right">{p('label_vat', { rate: defaultVatRate })}</label>
                              <input
                                type="number"
                                value={newBon.tva}
                                onChange={(e) => {
                                  const ht = parseFloat(newBon.montantHT) || 0;
                                  const tva = parseFloat(e.target.value) || 0;
                                  const ttc = ht + (ht * tva / 100);
                                  setNewBon({ 
                                    ...newBon, 
                                    tva: e.target.value,
                                    montantTTC: ttc.toFixed(2)
                                  });
                                }}
                                placeholder={String(defaultVatRate)}
                                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              />
                            </div>
                            
                            <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                              <label className="block w-full text-white/70 text-sm font-medium text-right">{p('label_amount_ttc')}</label>
                              <input
                                type="number"
                                value={newBon.montantTTC}
                                readOnly
                                placeholder={p('placeholder_amount')}
                                className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 cursor-not-allowed"
                              />
                            </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </QawaemErpListPageShell>
      {showViewModal && selectedBon ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={`${p('action_view')}: ${selectedBon.invoiceNumber}`}
          icon={Wallet}
          maxWidth="900px"
        >
          <div className={CAISSE_FORM_GRID}>
            <div>
              <p className="text-white/60 text-sm mb-1">{p('col_number')}</p>
              <p className="text-white font-mono font-semibold">{selectedBon.invoiceNumber}</p>
            </div>
            <div>
              <p className="text-white/60 text-sm mb-1">{p('col_date')}</p>
              <p className="text-white font-medium">{new Date(selectedBon.date).toLocaleDateString(dateLocale)}</p>
            </div>
            <div>
              <p className="text-white/60 text-sm mb-1">{p('col_beneficiary')}</p>
              <p className="text-white font-medium">{selectedBon.clientName}</p>
            </div>
            <div>
              <p className="text-white/60 text-sm mb-1">{p('col_type')}</p>
              <p className="text-white font-medium">
                {selectedBon.type === 'petty_cash' ? p('type_petty_cash') : selectedBon.type === 'expense' ? p('type_expense') : p('type_advance')}
              </p>
            </div>
            <div>
              <p className="text-white/60 text-sm mb-1">{p('col_amount')}</p>
              <p className="text-white font-bold text-xl">{formatAmount(selectedBon.total)}</p>
            </div>
            <div>
              <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
              <p className="text-white font-medium">{selectedBon.status === 'completed' ? p('status_completed') : p('status_pending')}</p>
            </div>
            {selectedBon.designation ? (
              <div className="md:col-span-3">
                <p className="text-white/60 text-sm mb-1">{p('label_designation')}</p>
                <p className="text-white/90">{selectedBon.designation}</p>
              </div>
            ) : null}
          </div>
        </QawaemErpViewModal>
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default GestionBonsPage;


